import { getPostPayments, GetPostPaymentsParams, PaginatedApiResponseData, PostPayment } from '@round/api';
import { useState, useCallback } from 'react';

type Params = GetPostPaymentsParams;

type State =
    | {
          data: null;
          status: 'not-initialized';
          error: null;
      }
    | {
          data: null;
          status: 'loading';
          error: null;
      }
    | {
          data: Pick<PaginatedApiResponseData<PostPayment>, 'results' | 'count'>;
          status: 'initialized';
          error: null;
      }
    | {
          data: null;
          status: 'error';
          error: string;
      };

const initialState: State = {
    status: 'not-initialized',
    data: null,
    error: null,
};

export default function usePostPayments() {
    const [state, setState] = useState<State>(initialState);

    const fetchData = useCallback(async (params: Params, requestInit?: RequestInit) => {
        try {
            setState({ status: 'loading', data: null, error: null });
            const response = await getPostPayments(params, requestInit);
            if (response.status === 404) {
                setState({ status: 'error', error: response.data.detail, data: null });
                return response;
            }

            setState({
                status: 'initialized',
                data: {
                    count: response.data.count,
                    results: response.data.results,
                },
                error: null,
            });

            return response;
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                throw e;
            }

            setState({ status: 'error', error: 'Could not fetch payments', data: null });
            throw e;
        }
    }, []);

    const reset = () => setState(initialState);

    return {
        ...state,
        fetchData,
        reset,
    };
}
